// INV26 Slice 3 (docs/build-briefs/INV26-backbar-inventory.md; DEC-206
// item 4, DEC-207) originally shipped this file's math as a POUR COST %
// calculator. INV27 (docs/build-briefs/INV27-item-editor-redesign.md;
// DEC-227 item 2) replaces that framing with MARKUP ON COST, per the
// owner's verbatim ruling 2026-09-29: "The percentage should be the
// margin above the fixed costs. Since we can't change our costs, we can
// change our margins per pour and bottle. So I would for instance like
// to set 100% for a 100% margin instead. I dont want percentage to
// represent cost is to pour. That seems backwards."
//
// Formula: new price = cost x (1 + markup% / 100), rounded UP to the
// next $0.25 (roundUpToQuarterCents below is unchanged from INV26 --
// only the input to it changed). Worked example from DEC-227: 100%
// markup on a $2.12/oz cost gives cost x 2 = $4.24 exact, which rounds
// UP to $4.25.
//
// Pure, dependency-free (no window/document access), so a node test can
// import it directly with no DOM (admin.ts itself is NOT DOM-free at
// import: its module-level mustFind(...) calls resolve real elements as
// soon as it loads).
//
// The ounces-per-bottle conversion reuses convertSizeToOunces
// (harness/src/domain/inventoryStockState.ts) exactly, per the original
// INV26 brief: "no new conversion constant is introduced." INV27 does
// not change this.

import { resolveOuncesPerContainerForPrice } from "../../src/domain/inventorySizeUnits.ts";
import {
  calculatedPerOuncePriceCents,
  calculatedUnitPriceCents,
} from "../../src/domain/catalogPriceMath.ts";

/**
 * Rounds a cents amount that may carry sub-cent floating-point error
 * (from the multiplication that produced it) up to the next 25-cent
 * ($0.25) increment, never down, and never adds a whole extra quarter
 * when the value already sits exactly on one.
 *
 * `exactCents` is first snapped to the nearest hundredth of a cent (a
 * resolution far finer than any real location price needs) to absorb
 * floating-point noise from the multiplication upstream; the ceiling
 * decision itself is then EXACT integer arithmetic done in BigInt,
 * `(a + b - 1) / b`, which cannot itself introduce rounding error. This
 * is the "exact integer math" the rounding rule requires: a value
 * already at a quarter-dollar boundary (754545455n / 25 exactly, say)
 * must not be bumped to the next one, and a value one cent above a
 * boundary must round all the way up to the NEXT quarter, not merely add
 * one cent.
 */
export function roundUpToQuarterCents(exactCents: number): number {
  if (!Number.isFinite(exactCents) || exactCents < 0) {
    throw new RangeError("roundUpToQuarterCents requires a finite, non-negative cents value");
  }
  const microCents = BigInt(Math.round(exactCents * 1_000_000));
  const quarterMicroCents = 25n * 1_000_000n;
  const steps = (microCents + quarterMicroCents - 1n) / quarterMicroCents;
  return Number(steps * 25n);
}

export interface PriceCalculatorInputs {
  bottlesPerOrderUnit?: string | null;
  /** The item's preferred vendor offer cost, in cents, for one full order-unit package
   * ( `vendor_item_offers.cost_cents`). Null when no
   * preferred vendor offer exists yet (CLAUDE.md's "never fall back to a
   * hardcoded figure": the calculator has nothing to compute, and shows
   * blank rather than $0.00). */
  unitCostCents: number | null;
  /** `liquor_products.size_value` / `size_unit`, unchanged. Bottles are
   * 750 ml today only because that is what is seeded; the ounces-per-
   * bottle figure is always derived from these two fields, never
   * hardcoded (this file never mentions 750 or any other size). */
  sizeValue: string | null;
  sizeUnit: string | null;
  /** KEGMATH1. For a keg the Size above is the SERVING, not the container: the
   * container volume is the keg size's volume_oz. These three let the one shared
   * rule (resolveOuncesPerContainerForPrice) pick the right source; the volume map comes
   * from the server's keg_size vocabulary (volumeOz), never a constant. Omitted
   * means not a keg. A keg with no keg size gets no price. */
  containerType?: string | null;
  defaultKegSize?: string | null;
  kegSizeVolumeOzByValue?: ReadonlyMap<string, string>;
  /** The item's CURRENT sell price per fl oz, in cents
   * (read-only current rate), used only to show "Markup today": the
   * markup this item is already charging, before any target is typed.
   * Null when the item has never been priced (e.g. a brand-new item). */
  currentPricePerOunceCents: number | null;
  /** The item's CURRENT sell price per whole unit/bottle, in cents
   * (`admin-bottle-unit-price`), used only to show "Markup today" for
   * the per-bottle column. */
  currentPricePerBottleCents: number | null;
  /** The target markup % the user typed for the "per fl oz" column,
   * e.g. 100 for a 100% markup ($4.25 on a $2.12 cost). Null until
   * entered, or when Settings has no default for this item's kind
   * (design principle 4, "every required choice is a full stop, never a
   * soft default": CLAUDE.md's "never fall back to a hardcoded figure"
   * means an item kind with no configured default opens blank, never a
   * guessed number). */
  targetMarkupPercentPerOunce: number | null;
  /** The target markup % the user typed for the "per bottle" column. */
  targetMarkupPercentPerBottle: number | null;
}

export interface PriceCalculatorColumnResult {
  newPriceCents: number;
  profitCents: number;
  /** Markup %, recomputed from `newPriceCents` (the ROUNDED price),
   * never from the exact pre-rounding price (DEC-207 item 2, carried
   * forward unchanged by DEC-227). */
  displayedMarkupPercent: number;
  /** False when cost is zero. The legacy numeric field is then ignored. */
  displayedMarkupAvailable: boolean;
}

export interface PriceCalculatorResult {
  ouncesPerBottle: number;
  /** Exact (unrounded) cost per fluid ounce, in cents. Displayed rounded
   * to the nearest cent, never up: only the NEW PRICE rounds up. */
  costPerOunceCentsExact: number;
  /** The bottle's own unit cost, unchanged, in cents. */
  costPerBottleCents: number;
  /** "Markup today", per fl oz: derived from the CURRENT sell price,
   * (price - cost) / cost x 100. Null when there is no current price to
   * derive it from. Shown regardless of whether a target markup has
   * been typed (DEC-227: "Owner said yes to showing the 'today'
   * figure"). */
  todayMarkupPercentPerOunce: number | null;
  /** "Markup today", per whole unit/bottle. */
  todayMarkupPercentPerBottle: number | null;
  perOunce: PriceCalculatorColumnResult | null;
  perBottle: PriceCalculatorColumnResult | null;
}

/**
 * Returns null when there is not enough data to compute even the
 * read-only Cost row (no preferred vendor offer, or a size/unit
 * `convertSizeToOunces` cannot convert). Once a Cost is available, a
 * missing, zero, or negative target markup % for a given column leaves
 * that column's own TARGET result null without failing the whole
 * calculator: the other column may still be usable independently,
 * matching the mockup's two independently-editable Target markup % cells,
 * and the "Markup today" figures render independently of either target.
 */
export function computePriceCalculator(
  inputs: PriceCalculatorInputs,
): PriceCalculatorResult | null {
  if (
    inputs.unitCostCents === null ||
    !Number.isFinite(inputs.unitCostCents) ||
    inputs.unitCostCents < 0
  ) {
    return null;
  }
  const ouncesStr = resolveOuncesPerContainerForPrice(
    {
      container_type: inputs.containerType ?? null,
      size_value: inputs.sizeValue,
      size_unit: inputs.sizeUnit,
      default_keg_size: inputs.defaultKegSize ?? null,
    },
    inputs.kegSizeVolumeOzByValue ?? new Map<string, string>(),
  );
  if (ouncesStr === null) {
    return null;
  }
  const ouncesPerBottle = Number(ouncesStr);
  if (!Number.isFinite(ouncesPerBottle) || ouncesPerBottle <= 0) {
    return null;
  }

  const pack = inputs.bottlesPerOrderUnit === undefined ? "1" : inputs.bottlesPerOrderUnit;
  if (pack === null || !Number.isFinite(Number(pack)) || Number(pack) <= 0) return null;
  const costPerBottleCents = inputs.unitCostCents / Number(pack);
  const costPerOunceCentsExact = costPerBottleCents / ouncesPerBottle;
  let perOunce: PriceCalculatorColumnResult | null = null;
  let perBottle: PriceCalculatorColumnResult | null = null;
  if (inputs.targetMarkupPercentPerOunce !== null) {
    try {
      const cents = calculatedPerOuncePriceCents({
        costCents: inputs.unitCostCents,
        bottlesPerOrderUnit: pack,
        ouncesPerContainer: ouncesStr,
        targetMarkupPercent: inputs.targetMarkupPercentPerOunce,
      });
      if (cents !== null) perOunce = calculatorColumn(costPerOunceCentsExact, cents);
    } catch {
      /* An incomplete target leaves only this column unavailable. */
    }
  }
  if (inputs.targetMarkupPercentPerBottle !== null) {
    try {
      perBottle = calculatorColumn(
        costPerBottleCents,
        calculatedUnitPriceCents(inputs.unitCostCents, pack, inputs.targetMarkupPercentPerBottle),
      );
    } catch {
      /* The other column remains independently usable. */
    }
  }

  return {
    ouncesPerBottle,
    costPerOunceCentsExact,
    costPerBottleCents,
    todayMarkupPercentPerOunce: computeTodayMarkup(
      costPerOunceCentsExact,
      inputs.currentPricePerOunceCents,
    ),
    todayMarkupPercentPerBottle: computeTodayMarkup(
      costPerBottleCents,
      inputs.currentPricePerBottleCents,
    ),
    perOunce,
    perBottle,
  };
}

function computeTodayMarkup(
  costCentsExact: number,
  currentPriceCents: number | null,
): number | null {
  if (
    currentPriceCents === null ||
    !Number.isFinite(currentPriceCents) ||
    currentPriceCents < 0 ||
    !Number.isFinite(costCentsExact) ||
    costCentsExact <= 0
  ) {
    return null;
  }
  return ((currentPriceCents - costCentsExact) / costCentsExact) * 100;
}

function calculatorColumn(
  costCentsExact: number,
  newPriceCents: number,
): PriceCalculatorColumnResult | null {
  if (!Number.isFinite(costCentsExact) || costCentsExact < 0) return null;
  return {
    newPriceCents,
    profitCents: newPriceCents - costCentsExact,
    // Zero is only a compatibility value when markup has no denominator.
    displayedMarkupPercent:
      costCentsExact === 0 ? 0 : ((newPriceCents - costCentsExact) / costCentsExact) * 100,
    displayedMarkupAvailable: costCentsExact > 0,
  };
}
